/**
 * GTM + GA4 Event Helpers
 *
 * Mirrors the pattern established in pixel.ts — safe to call on the server
 * (they no-op when `window` is not available) so they can be imported freely
 * in client components without extra guards at the call site.
 *
 * Every public function pushes to BOTH:
 *   1. window.gtag(...)     → for Google Analytics 4
 *   2. window.dataLayer.push → for Google Tag Manager
 */

// ─── low-level guards ────────────────────────────────────────────────────────

/** No-op on server and when gtag hasn't loaded yet. */
function gtagEvent(eventName: string, params?: Record<string, unknown>) {
  if (typeof window === 'undefined' || typeof (window as any).gtag !== 'function') return;
  (window as any).gtag('event', eventName, params);
}

/** No-op on server. Always safe — dataLayer is initialised by the GTM snippet. */
function dataLayerPush(data: Record<string, unknown>) {
  if (typeof window === 'undefined') return;
  (window as any).dataLayer = (window as any).dataLayer || [];
  (window as any).dataLayer.push(data);
}

// ─── helpers ─────────────────────────────────────────────────────────────────

function buildItems(contentIds: (string | number)[], names?: (string | undefined)[]) {
  return contentIds.map((id, i) => {
    const item: Record<string, unknown> = { item_id: String(id) };
    if (names?.[i]) item['item_name'] = names[i];
    return item;
  });
}

// ─── public API ──────────────────────────────────────────────────────────────

// ---------- Page View --------------------------------------------------------

export function analyticsPageView() {
  gtagEvent('page_view');
  dataLayerPush({ event: 'page_view' });
}

// ---------- View Item (product detail) ---------------------------------------

export interface AnalyticsViewItemParams {
  id: string | number;
  name: string;
  category?: string;
  value?: number;
  currency?: string;
}

export function analyticsViewItem({ id, name, category, value, currency = 'ARS' }: AnalyticsViewItemParams) {
  const items = [{ item_id: String(id), item_name: name, ...(category ? { item_category: category } : {}) }];

  const params: Record<string, unknown> = {
    currency,
    items,
  };
  if (value != null) params['value'] = value;

  gtagEvent('view_item', params);
  dataLayerPush({ event: 'view_item', ecommerce: params });
}

// ---------- Add to Cart ------------------------------------------------------

export interface AnalyticsAddToCartParams {
  contentIds: (string | number)[];
  numItems: number;
  value: number;
  currency?: string;
}

export function analyticsAddToCart({ contentIds, numItems, value, currency = 'ARS' }: AnalyticsAddToCartParams) {
  const items = buildItems(contentIds);

  const params: Record<string, unknown> = {
    currency,
    value,
    items,
    num_items: numItems,
  };

  gtagEvent('add_to_cart', params);
  dataLayerPush({ event: 'add_to_cart', ecommerce: params });
}

// ---------- Begin Checkout ---------------------------------------------------

export interface AnalyticsBeginCheckoutParams {
  contentIds: (string | number)[];
  numItems: number;
  value: number;
  currency?: string;
}

export function analyticsBeginCheckout({ contentIds, numItems, value, currency = 'ARS' }: AnalyticsBeginCheckoutParams) {
  const items = buildItems(contentIds);

  const params: Record<string, unknown> = {
    currency,
    value,
    items,
    num_items: numItems,
  };

  gtagEvent('begin_checkout', params);
  dataLayerPush({ event: 'begin_checkout', ecommerce: params });
}

// ---------- Add Payment Info -------------------------------------------------

export function analyticsAddPaymentInfo() {
  gtagEvent('add_payment_info');
  dataLayerPush({ event: 'add_payment_info' });
}

// ---------- Purchase ---------------------------------------------------------

export interface AnalyticsPurchaseParams {
  orderId: string;
  value: number;
  currency?: string;
  contentIds?: (string | number)[];
  numItems?: number;
}

export function analyticsPurchase({ orderId, value, currency = 'ARS', contentIds = [], numItems }: AnalyticsPurchaseParams) {
  const items = buildItems(contentIds);

  const params: Record<string, unknown> = {
    transaction_id: orderId,
    currency,
    value,
    items,
  };
  if (numItems != null) params['num_items'] = numItems;

  gtagEvent('purchase', params);
  dataLayerPush({ event: 'purchase', ecommerce: params });
}

// ---------- Lead -------------------------------------------------------------

export interface AnalyticsLeadParams {
  value?: number;
  currency?: string;
}

export function analyticsLead({ value, currency = 'ARS' }: AnalyticsLeadParams = {}) {
  const params: Record<string, unknown> = {};
  if (value != null) {
    params['value'] = value;
    params['currency'] = currency;
  }

  gtagEvent('lead', params);
  dataLayerPush({ event: 'lead', ...params });
}

// ---------- Complete Registration --------------------------------------------

export function analyticsCompleteRegistration() {
  gtagEvent('sign_up', { method: 'checkout' });
  dataLayerPush({ event: 'sign_up', method: 'checkout' });
}

// ---------- Contact ----------------------------------------------------------

export function analyticsContact() {
  gtagEvent('contact');
  dataLayerPush({ event: 'contact' });
}
